/**
 * Which business kind IS this workspace — the one resolution, shared.
 *
 * Split out of `businessPresets.ts` (round 3 of review, pure move). That file
 * had become two things: a static table mirroring the server's presets, and
 * this — a store with a real lifecycle (module state, a deadline racing a
 * request, a parked supersede, generation fencing, a workspace-change
 * subscription). At 488 of the repo's 500-line ceiling there was no headroom
 * left for either of them.
 *
 * The kind VOCABULARY lives here rather than beside the preset table because
 * this module is the one that validates it: `businessType` arrives from the
 * server as an untrusted string and is narrowed here before anything reads a
 * preset with it. `businessPresets.ts` types `CLIENT_PRESETS` as
 * `Record<BusinessTypeId, ...>`, so the table cannot drift from this list
 * without a type error.
 *
 * Every consumer still imports `useBusinessType` from `businessPresets.ts`,
 * which re-exports it — no call site moved.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { workspacesApi } from '../services/api/authApi';
import { onWorkspaceChange } from '../services/api/core';
import { JOIN_WINDOW_MS } from '../services/api/coalesce';
import { storageGet } from '../lib/nativeStorage';

/**
 * The kind vocabulary, as a runtime list so it can narrow a server string, and
 * as the type derived from it so the two can never disagree. Ids MUST match
 * the server (server/src/config/businessPresets.ts, guarded there by
 * businessPresets.test.ts).
 */
export const BUSINESS_TYPE_IDS = [
  'ecommerce', 'service', 'course', 'saas', 'coaching', 'content_creator',
] as const;

export type BusinessTypeId = (typeof BUSINESS_TYPE_IDS)[number];

function isBusinessTypeId(value: string | undefined): value is BusinessTypeId {
  return value !== undefined && (BUSINESS_TYPE_IDS as readonly string[]).includes(value);
}

export type BusinessTypeSource = 'default' | 'detected' | 'manual';

/**
 * How much we actually know about this workspace's kind.
 *
 * `'error'` is the whole point of this type. Before it existed, a failed
 * `GET /workspaces/:id` left `businessType` at its `'ecommerce'` initial value
 * with `loading` false, so a 429 was INDISTINGUISHABLE from a successful read
 * of a real e-commerce workspace. `businessTypeSource` did not disambiguate it
 * either — an undetected e-commerce workspace also reads `'default'`. That is
 * not merely cosmetic: `SetupChecklist` persists step completions keyed by
 * kind, so a services merchant who hit one 429 wrote setup progress under
 * `kind: 'ecommerce'`, where their real checklist will never see it.
 *
 * `businessType` deliberately stays non-nullable. Callers that only present
 * things (tab labels, nouns, which overview to render) keep reading it and keep
 * falling back to e-commerce; callers that WRITE per-kind data must require
 * `status === 'ready'` — POSITIVELY, not `!== 'error'`. Round 1 of review
 * caught exactly that mistake: `'loading'` is equally "we do not know", and a
 * `!== 'error'` gate let a tick land under the fallback kind during the window
 * after `invalidateBusinessType()`.
 */
export type BusinessTypeStatus = 'loading' | 'ready' | 'error';

export interface BusinessTypeState {
  /** Always a valid kind. On 'loading'/'error' this is the presentation fallback, NOT a fact. */
  businessType: BusinessTypeId;
  businessTypeSource: BusinessTypeSource;
  status: BusinessTypeStatus;
  /** `status === 'loading'`, kept so existing callers need no change. */
  loading: boolean;
  /**
   * The workspace's own name, from the same `GET /workspaces/:id` read — so
   * the hub can label itself without a second request. Null until that read
   * succeeds; never a placeholder, so callers choose their own fallback.
   */
  workspaceName: string | null;
  /**
   * A live store connector exists. Read off the same GET /workspaces/:id, so it
   * costs no request. The hub shows Inventory and Orders whenever this is true,
   * whatever the kind — a coaching workspace with a Shopify store still sells.
   */
  storeConnected: boolean;
  /**
   * The workspace's ISO 4217 currency, off the same GET /workspaces/:id. The
   * Hub's money tiles format with it; they used to read GET /settings/brand,
   * which has no currency, and showed an ILS store's revenue in dollars.
   * Null until that read succeeds.
   */
  currency: string | null;
}

const UNRESOLVED: BusinessTypeState = {
  businessType: 'ecommerce',
  businessTypeSource: 'default',
  status: 'loading',
  loading: true,
  workspaceName: null,
  storeConnected: false,
  currency: null,
};

/**
 * ONE RESOLUTION, SHARED.
 *
 * Fifteen components call `useBusinessType`. Each used to own an effect and
 * therefore its own `GET /workspaces/:id`; task 72's single-flight coalescing
 * merged the simultaneous ones but not the ones a render wave apart, leaving a
 * named residual of 2 on the hub's budget. A module-level store removes the
 * duplication at the source instead of downstream: the answer is fetched once
 * and every consumer reads the same snapshot, so one retry fixes all of them
 * rather than one of them.
 *
 * It is a resolution, not a cache. Three things keep it from becoming one:
 *
 *  - `settledFor` pins the answer to the workspace it was read for, and the
 *    store resets on `onWorkspaceChange` so one account's kind can never render
 *    for the next on a shared browser (login and logout are SPA navigation —
 *    module state survives them; only the workspace SWITCHER hard-reloads).
 *  - `invalidateBusinessType()` drops the answer AND immediately starts a new
 *    read, so a merchant who changes their kind in Account sees every mounted
 *    consumer follow rather than sitting on a stale — or worse, a defaulted —
 *    value until something remounts.
 *  - `REVALIDATE_AFTER_MS` — see below. The kind is also rewritten SERVER-side
 *    and asynchronously, and nothing tells the client when.
 */
let state: BusinessTypeState = UNRESOLVED;
/** Workspace id the current `'ready'` snapshot describes; null when not ready. */
let settledFor: string | null = null;
/**
 * When the last attempt FINISHED, whatever its outcome — not when the last
 * answer arrived.
 *
 * Round 2 of review: stamping this only on success meant a failing background
 * re-check never moved the horizon, so every later mount wave asked again.
 * Measured against a persistent 429 with the clock frozen: 2 -> 3 -> 4 requests
 * over three navigations. Still fewer than the per-consumer effects this store
 * replaced, and self-healing rather than a poll — but this codebase has just
 * finished paying for a rate-limit incident, and "we tried recently" is the
 * honest bound on asking again, not "we succeeded recently".
 */
let lastAttemptAt = 0;
let inFlight: Promise<void> | null = null;
/**
 * A read that was on the wire when an invalidation superseded it, parked until
 * something actually starts the replacement.
 *
 * Round 2 of review: this used to be a local in `invalidateBusinessType`, handed
 * straight to `startResolution` — so it was DROPPED whenever nothing was
 * subscribed. Mount, unmount mid-read, invalidate, mount again, and the new
 * read joined the pre-invalidation flight through `apiRequest`'s single-flight
 * and published its stale kind as `'ready'`, where the write gate accepts it.
 * Module scope so the next resolution consumes it no matter who starts it.
 */
let pendingSupersede: Promise<void> | null = null;
/**
 * Bumped by every reset. A fetch that was already on the wire when the kind (or
 * the workspace) changed underneath it must not publish its now-stale answer.
 */
let generation = 0;
const listeners = new Set<() => void>();

/**
 * How long a settled answer is reused before the next MOUNT re-reads it in the
 * background.
 *
 * WHY THIS IS NOT A TTL BOLTED ONTO A CACHE. The kind is not only written by
 * the three client sites that call `invalidateBusinessType()`. The server
 * rewrites it on its own — `applyFromConnector` after an OAuth connect, the
 * Cal.com/Calendly/Stripe/course webhooks, the pack installer — and those are
 * exactly the writes that produce `businessTypeSource: 'detected'`, the value
 * the hub advertises as "set up as a {type} business from your connections".
 * Nothing pushes that to the client.
 *
 * Before this store existed, every consumer owned an effect, so ANY later mount
 * re-read the workspace and picked such a change up. Sharing one resolution
 * without this horizon would have quietly converted that into "wrong until a
 * hard reload" — a regression in the happy path, which is the one thing this
 * task was told not to move. So the horizon restores the old propagation rule
 * (a later mount re-reads) while keeping the duplicate-removal that a single
 * page load needs.
 *
 * Thirty seconds is chosen against those two bounds, not by taste: a page's
 * whole mount cascade is milliseconds, so no page load ever pays twice and the
 * hub's budget of 1 is safe; every workflow that could have changed the kind —
 * an OAuth round trip, a webhook, a pack install — takes far longer than this,
 * so returning to a tab and navigating picks the new kind up.
 */
const REVALIDATE_AFTER_MS = 30_000;

function publish(next: BusinessTypeState): void {
  state = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Identity-stable between publishes — `useSyncExternalStore` requires that. */
function getSnapshot(): BusinessTypeState {
  return state;
}

/**
 * `background: true` means "we already hold a real answer and are only checking
 * it". Such a read must not publish `'loading'` on the way (every consumer
 * would flash a skeleton on every navigation) and must not publish `'error'` if
 * it fails — a failed re-check does not unlearn an answer that was genuinely
 * read from the server, and downgrading to `'error'` would needlessly freeze
 * the setup checklist for a merchant whose kind we do know.
 */
/**
 * How long to keep saying `'loading'` before calling it a failure.
 *
 * `apiRequest` has no timeout and no abort — coalesce.ts:32-33 says so — and a
 * laptop waking onto captive-portal wifi opens a socket that neither answers
 * nor errors. Without a bound here, `'loading'` is TERMINAL: `inFlight` never
 * clears, `retryBusinessType()` no-ops behind its own guard, and because the
 * retry affordance is deliberately shown only for `'error'`, the merchant is
 * left with the "we couldn't confirm your business kind" notice and no way out
 * short of reloading the page. Round 2 of review found exactly that wedge.
 *
 * The number is not a new judgement: it is `coalesce.ts`'s join window, the
 * point at which the shared-read layer itself stops offering a flight to new
 * callers because it has stopped looking like it will answer. Past that instant
 * there is nothing left to join and no reason to keep claiming we are loading.
 * One number, one rationale, and it moves if that one moves.
 *
 * As there, the REQUEST is not bounded — only our willingness to wait. If the
 * answer arrives late it still publishes, because a real answer beats a
 * timeout.
 *
 * THE INVARIANT, AND WHY IT IS NOT JUST A COMMENT. This must never be SHORTER
 * than `JOIN_WINDOW_MS`. If it were, we would publish `'error'` — and offer the
 * merchant a retry — while the dead flight was still joinable, so the retry
 * would join it and hang too, which is the wedge again with an extra click in
 * it. Writing it as a direct import makes that true by construction today, but
 * only today: a future edit that gives this module its own constant would be
 * invisible. `businessTypeDeadline.test.tsx` exercises the tie itself.
 */
export const READ_DEADLINE_MS = JOIN_WINDOW_MS;

function deadline(ms: number): { promise: Promise<void>; cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const promise = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, ms);
  });
  return {
    promise,
    cancel: () => {
      if (timer !== undefined) clearTimeout(timer);
    },
  };
}

async function readWorkspaceKind(background: boolean): Promise<void> {
  const startedAt = generation;
  const workspaceId = storageGet('buzzbit_workspace');
  const failed = (): void => {
    if (startedAt !== generation || background) return;
    settledFor = null;
    publish({ businessType: 'ecommerce', businessTypeSource: 'default', status: 'error', loading: false, workspaceName: null, storeConnected: false, currency: null });
  };

  // No workspace selected is not "e-commerce" either — it is the same "we do
  // not know", and the same rule applies: nothing per-kind may be written.
  // Retry re-reads storage, so selecting a workspace recovers.
  if (!workspaceId) {
    failed();
    return;
  }

  let answered = false;
  const answer = workspacesApi.get(workspaceId).then(
    (res) => {
      answered = true;
      if (startedAt !== generation) return;
      const kind = res.data?.businessType as BusinessTypeId | undefined;
      const src = res.data?.businessTypeSource as BusinessTypeSource | undefined;
      const name = typeof res.data?.name === 'string' ? res.data.name.trim() : '';
      settledFor = workspaceId;
      publish({
        // An absent or unrecognised kind on a SUCCESSFUL read is a real answer
        // of "e-commerce, undetected" — exactly what it resolved to before.
        businessType: isBusinessTypeId(kind) ? kind : 'ecommerce',
        businessTypeSource: src === 'default' || src === 'detected' || src === 'manual' ? src : 'default',
        status: 'ready',
        loading: false,
        workspaceName: name || null,
        storeConnected: res.data?.storeConnected === true,
        currency: typeof res.data?.currency === 'string' && res.data.currency ? res.data.currency : null,
      });
    },
    () => {
      answered = true;
      failed();
    },
  );

  const limit = deadline(READ_DEADLINE_MS);
  await Promise.race([answer, limit.promise]);
  limit.cancel();
  // The deadline won. Stop waiting — but `answer` is still live above, so a
  // late reply can still overwrite this with the truth.
  if (!answered) failed();
}

function startResolution(background: boolean): void {
  if (inFlight) return;

  // A foreground restart must not keep serving the answer it is replacing.
  // Round 2 of review: where `ensureResolved` finds a `'ready'` snapshot whose
  // `settledFor` no longer matches storage — `ghostProtocol.ts:37-40,64-66`
  // writes `buzzbit_workspace` through raw localStorage and bypasses
  // `setWorkspaceId`, and nothing listens for cross-tab `storage` events — the
  // store went on publishing the PREVIOUS workspace's kind as `'ready'` for the
  // whole re-read, and the write gate accepts `'ready'`. That was a regression
  // against round 0, which at least said `'loading'` there.
  if (!background) publish(UNRESOLVED);

  // Consumed here rather than passed in, so a supersede parked while nothing
  // was subscribed is still honoured by whoever starts the next read.
  const after = pendingSupersede;
  pendingSupersede = null;

  const run = (): Promise<void> => readWorkspaceKind(background);
  const attempt = after ? after.then(run, run) : run();
  inFlight = attempt;
  // Identity, not presence — the same rule coalesce.ts's `release` follows. A
  // superseded attempt settling late must not clear the resolution that
  // replaced it, or the next caller would start a redundant second read.
  void attempt.finally(() => {
    lastAttemptAt = Date.now();
    if (inFlight === attempt) inFlight = null;
  });
}

/**
 * Called by every consumer on mount.
 *
 *  - no usable answer (first mount, or the last read failed) -> resolve, and
 *    say `'loading'` while doing it. Re-attempting after an error preserves the
 *    self-healing the per-component effects used to give for free.
 *  - a usable answer older than the horizon -> re-read in the BACKGROUND,
 *    keeping the current snapshot on screen.
 *  - a usable answer inside the horizon -> nothing.
 *
 * Every consumer in a mount wave lands in the same `inFlight`, so a wave costs
 * one request, not fifteen.
 */
function ensureResolved(): void {
  const usable = state.status === 'ready' && settledFor === storageGet('buzzbit_workspace');
  if (!usable) {
    startResolution(false);
    return;
  }
  if (Date.now() - lastAttemptAt > REVALIDATE_AFTER_MS) startResolution(true);
}

/**
 * Re-run the read after a failure. Surfaced in the Business Hub header and the
 * setup checklist.
 *
 * The `inFlight` guard inside `startResolution` makes this a no-op while a read
 * is genuinely on the wire, which is correct rather than a trap: that read is
 * bounded by `READ_DEADLINE_MS`, so `'error'` — and with it this button —
 * always comes back. `generation` is bumped so a straggler from the attempt
 * that just failed cannot land on top of this one's answer.
 */
export function retryBusinessType(): void {
  if (inFlight) return;
  generation += 1;
  startResolution(false);
}

/**
 * Forget the answer and go get a new one.
 *
 * It STARTS the read rather than waiting for a mount, and that is the whole
 * point. Round 1 of review: an earlier version only published the unresolved
 * snapshot, and since `ensureResolved` runs in the hook's mount effect,
 * already-mounted consumers — `SetupChecklist` is mounted on every
 * authenticated route — sat on `loading|ecommerce` indefinitely after a
 * merchant changed their kind. With the write gate then reading `!== 'error'`,
 * ticking a step filed it under `kind: 'ecommerce'` for a merchant who had just
 * said they were not an e-commerce business. The defect this task exists to fix,
 * reintroduced by the fix, and without even needing a 429.
 *
 * The read is started only when something is actually SUBSCRIBED. Mounted
 * consumers are the ones with no mount left to trigger `ensureResolved` and so
 * the ones that need it; with nothing mounted there is no one to tell, and the
 * next mount resolves anyway. That also keeps logout — which invalidates via
 * `onWorkspaceChange` — from spending a request nobody asked for.
 *
 * Call after writing `businessType` on the workspace. It is also the seam tests
 * use to get a cold store between cases.
 */
export function invalidateBusinessType(): void {
  // A read issued BEFORE the write that triggered this invalidation can still
  // be on the wire, and it is carrying the OLD kind. Starting the replacement
  // straight away would not open a second connection — task 72's `apiRequest`
  // single-flight would JOIN the stale one and publish its answer as `'ready'`,
  // which is precisely the "confidently wrong" outcome this task exists to
  // remove. So the replacement waits for the old flight to settle and release
  // its coalescing entry, and only then asks.
  if (inFlight) pendingSupersede = inFlight;
  generation += 1;
  settledFor = null;
  lastAttemptAt = 0;
  inFlight = null;
  publish(UNRESOLVED);
  if (listeners.size > 0) startResolution(false);
}

// One account's kind must never render for the next on a shared browser.
// `storageGet('buzzbit_workspace')` outlives the session, and logout
// (TopBar) and login (Login) are SPA navigation — this module is not
// re-evaluated, so without this the previous tenant's answer survives into the
// next tenant's first paint. The existing seam, used the way creativeStore
// uses it.
onWorkspaceChange(() => {
  invalidateBusinessType();
});

/**
 * The current workspace's name, read from the one shared resolution above —
 * no request of its own. Null while loading or after a failed read.
 */
export function useWorkspaceName(): string | null {
  return useBusinessType().workspaceName;
}

export function useBusinessType(): BusinessTypeState & { retry: () => void } {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot);
  useEffect(() => {
    ensureResolved();
  }, []);
  return { ...snapshot, retry: retryBusinessType };
}
